<template lang="pug">
  #repository
    .repo-header.px-3.px-md-5
      .repo-header-inner
        h1 REPOSITORIO
        p.repo-lead
          | Informes, anexos y respuestas a solicitudes de información sobre
          |  hallazgos de fosas clandestinas en México.
    .repo-container.container-fluid.px-0
      b-overlay(
        :show="showLoading"
        variant="dark"
        opacity="0.6"
        blur="2px"
        rounded="sm")
      .row.mx-0
        aside.filters.col-12.col-md-3(
          ref="filters"
          :class="{ open: showMobileFilters }"
        )
          .filters-inner
            .filter-header
              p.bold-font.mb-0 Filtros
              button.clean(
                v-if="activeFilters.length > 0 || searchQuery !== ''"
                @click="clearFilters"
              ) Limpiar todo
            .filter-block(
              v-for="filter in filters"
              :key="filter.id"
              :class="filter.customClass"
            )
              template(v-if="filter.inputType === 'checkbox-group'")
                p.filter-label.bold-font.mb-2 {{ filter.label }}
                label.filter-option(
                  v-for="child in filter.inputChilds"
                  :key="child.key"
                  :class="child.key"
                )
                  input(
                    type="checkbox"
                    v-model="filter.model"
                    :value="child.key"
                    @change="filterDocuments()"
                  )
                  span.checkbox-mark
                  span.checkbox-dot(
                    v-if="child.labelColor !== '#ffffff'"
                    :style="{ backgroundColor: child.labelColor }"
                  )
                  span.option-label {{ child.value }}
                  span.option-count {{ facetCount(filter.filterKey, child.key) }}
              template(v-else-if="filter.inputType === 'autocomplete'")
                p.filter-label.bold-font.mb-2 {{ filter.label }}
                autocomplete(
                  :filterId="filter.id"
                  :items="filter.items ? filter.items : states"
                  :value="filter.model"
                  :text="filter.optionsValue"
                  :action="filter.action"
                  :clearAction="filter.clearAction"
                )

        .documents.col-12.col-md-9
          .toolbar
            .search-box
              b-icon(icon="search")
              input(
                type="search"
                v-model="searchQuery"
                @input="onSearch"
                placeholder="Buscar por título, entidad o folio…"
                aria-label="Buscar documentos"
              )
            p.results-count.bold-font.mb-0
              | {{ resultsLabel }}
          .active-filters(v-if="activeFilters.length > 0")
            button.chip(
              v-for="chip in activeFilters"
              :key="chip.filter.id + '-' + chip.value"
              @click="removeActiveFilter(chip)"
            )
              span.chip-dot(
                v-if="chip.color"
                :style="{ backgroundColor: chip.color }"
              )
              span {{ chip.label }}
              b-icon(icon="x-circle")
          .mobile-filters-row
            button.filters-toggle(
              ref="showFilters"
              @click="showMobileFilters = !showMobileFilters"
            )
              span Filtros
              b-icon(icon="filter-circle" font-scale="1.2")

          .doc-list
            .no-documents(v-if="docsByPage.length === 0 && !showLoading")
              h4.mb-2 No hay documentos
              p.mb-0 Ajusta los filtros o el texto de búsqueda.
            article.doc-row(
              v-for="documentInfo in docsByPage"
              :key="documentInfo.id"
            )
              .doc-color(:style="{ backgroundColor: documentInfo.color }")
              .doc-main
                .doc-badges
                  span.doc-type(
                    :style="{ backgroundColor: documentInfo.color }"
                  ) {{ documentInfo.nametype }}
                  span.doc-meta {{ documentInfo.year }}
                  span.doc-meta.doc-entity {{ documentInfo.entity }}
                p.doc-title.bold-font.mb-1 {{ documentInfo.title }}
                p.doc-subtitle.mb-0 {{ documentInfo.subtitle }}
              .doc-actions
                span.doc-file {{ fileExtension(documentInfo.datatype) }}
                button.doc-download.bold-font(
                  @click="downloadDocument(documentInfo)"
                )
                  b-icon(icon="download")
                  span Descargar

          .pagination.row.mx-0(v-if="numPages > 1")
            nav(aria-label="paginación")
              ul.pagination.mb-0
                li.page-item
                  button.page-link(
                    @click="previousPage"
                    :disabled="currentPage === 1"
                    aria-label="Página anterior"
                  )
                    b-icon(icon="caret-left-fill")
                template(v-for="(page, index) in pages")
                  li.page-item(
                    v-if="page === 'gap'"
                    :key="'gap-' + index"
                    disabled
                  )
                    span.page-link …
                  li.page-item(
                    v-else
                    :key="'page-' + page"
                    :class="{ active: page === currentPage }"
                  )
                    button.page-link(
                      @click="changePage(page)"
                      :aria-current="page === currentPage ? 'page' : null"
                    ) {{ page }}
                li.page-item
                  button.page-link(
                    @click="nextPage"
                    :disabled="currentPage === numPages"
                    aria-label="Página siguiente"
                  )
                    b-icon(icon="caret-right-fill")
</template>

<script>
import autocomplete from "@/components/Autocomplete";

const MIME_LABELS = {
  "application/pdf": "PDF",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "XLSX",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "DOCX",
  "application/msword": "DOC"
};

const normalize = value =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

export default {
  name: "Repository",
  components: {
    autocomplete
  },
  data() {
    return {
      filteredDocuments: [],
      showMobileFilters: false,
      searchQuery: "",
      docsByPage: [],
      numPages: 0,
      currentPage: 1,
      showLoading: false
    };
  },
  computed: {
    mobile() {
      return this.$store.state.mobile;
    },
    states() {
      const sortByName = (a, b) =>
        a.full_name.localeCompare(b.full_name, "es", { sensitivity: "base" });
      let filterStates = this.$store.state.entities
        .filter(e => e.id_mun === "NA")
        .slice()
        .sort(sortByName);
      filterStates.unshift({
        full_name: "Federal",
        id_ent: "00",
        id_mun: "00",
        name_ent: "NA",
        name_mun: "NA"
      });
      filterStates.unshift({
        full_name: "Sin Entidad",
        id_ent: "NA",
        id_mun: "NA",
        name_ent: "NA",
        name_mun: "NA"
      });
      return filterStates;
    },
    filters() {
      return this.$store.state.filters;
    },
    numDocs() {
      return this.mobile ? 8 : 12;
    },
    resultsLabel() {
      const total = this.filteredDocuments.length;
      if (total === 0) return "0 documentos";
      const lower = (this.currentPage - 1) * this.numDocs + 1;
      const upper = Math.min(this.currentPage * this.numDocs, total);
      return `${total} documentos · mostrando ${lower}–${upper}`;
    },
    activeFilters() {
      const chips = [];
      (this.filters || []).forEach(filter => {
        if (
          filter.inputType === "checkbox-group" &&
          Array.isArray(filter.model)
        ) {
          filter.model.forEach(value => {
            const child = (filter.inputChilds || []).find(c => c.key === value);
            chips.push({
              filter,
              value,
              label: child ? child.value : value,
              color:
                child && child.labelColor !== "#ffffff"
                  ? child.labelColor
                  : null
            });
          });
        } else if (filter.inputType === "autocomplete" && filter.model) {
          chips.push({
            filter,
            value: filter.model,
            label: filter.model,
            color: null
          });
        }
      });
      return chips;
    },
    pages() {
      const total = this.numPages;
      const current = this.currentPage;
      const result = [];
      if (total <= 7) {
        for (let i = 1; i <= total; i++) result.push(i);
        return result;
      }
      let start = Math.max(2, current - 1);
      let end = Math.min(total - 1, current + 1);
      if (current <= 3) {
        start = 2;
        end = 4;
      }
      if (current >= total - 2) {
        start = total - 3;
        end = total - 1;
      }
      result.push(1);
      if (start > 2) result.push("gap");
      for (let i = start; i <= end; i++) result.push(i);
      if (end < total - 1) result.push("gap");
      result.push(total);
      return result;
    }
  },
  methods: {
    /*
      WARNING: This function just works with empty arrays, check later
      for compatibility with other values
    */
    filterDocuments() {
      let documents = this.$store.state.documents || [];
      let filtersActive = (this.filters || []).filter(f => {
        return Array.isArray(f.model)
          ? f.model.length !== 0
          : f.model !== "" && f.model !== null && f.model !== undefined;
      });

      if (filtersActive.length > 0) {
        filtersActive.forEach(fa => {
          documents = documents.filter(d => {
            if (Array.isArray(fa.model)) {
              return fa.model.indexOf(d[fa.filterKey]) != -1;
            }
            return d[fa.filterKey] === fa.model;
          });
        });
      }

      const query = normalize(this.searchQuery.trim());
      if (query) {
        documents = documents.filter(d => {
          return normalize(
            `${d.title} ${d.subtitle} ${d.entity} ${d.nametype}`
          ).includes(query);
        });
      }

      documents = [...documents].sort((a, b) => {
        const yearDiff = Number(b.year) - Number(a.year);
        if (yearDiff !== 0) return yearDiff;
        return String(a.title).localeCompare(String(b.title), "es");
      });

      this.filteredDocuments = documents;
      this.numPages = Math.ceil(documents.length / this.numDocs);
      if (this.currentPage > this.numPages) {
        this.currentPage = this.numPages || 1;
      }
      const docsRange = this.getRangeForFilteredDocs(
        this.currentPage,
        this.numDocs
      );
      this.docsByPage = documents.slice(docsRange.lower, docsRange.upper);
    },
    getRangeForFilteredDocs(page, numDocuments) {
      let upperDocDivision = page * numDocuments;
      let lowestDocDivision = page * numDocuments - numDocuments;
      return {
        upper: upperDocDivision,
        lower: lowestDocDivision
      };
    },
    facetCount(filterKey, value) {
      const documents = this.$store.state.documents;
      if (!Array.isArray(documents)) return 0;
      return documents.filter(d => d[filterKey] === value).length;
    },
    fileExtension(datatype) {
      return MIME_LABELS[datatype] || "ARCHIVO";
    },
    onSearch() {
      this.currentPage = 1;
      this.filterDocuments();
    },
    nextPage() {
      if (this.currentPage < this.numPages) {
        this.currentPage = this.currentPage + 1;
        this.filterDocuments();
      }
    },
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage = this.currentPage - 1;
        this.filterDocuments();
      }
    },
    changePage(pageNumber) {
      if (pageNumber !== this.currentPage) {
        this.currentPage = pageNumber;
        this.filterDocuments();
      }
    },
    removeActiveFilter(chip) {
      if (Array.isArray(chip.filter.model)) {
        chip.filter.model = chip.filter.model.filter(v => v !== chip.value);
        this.currentPage = 1;
        this.filterDocuments();
      } else {
        this.$store.dispatch("clearDocStateFilter", {
          value: "",
          textParam: chip.filter.optionsValue,
          filterId: chip.filter.id
        });
        this.currentPage = 1;
      }
    },
    clearFilters() {
      (this.filters || []).forEach(filter => {
        filter.model = Array.isArray(filter.model) ? [] : "";
      });
      this.searchQuery = "";
      this.currentPage = 1;
      this.filterDocuments();
    },
    async downloadDocument(documentInfo) {
      this.showLoading = true;
      try {
        let response = await this.$store.dispatch(
          "getDocument",
          documentInfo.id
        );

        const base64ToBlob = (base64String, contentType = "") => {
          const byteCharacters = atob(base64String);
          const byteArrays = [];

          for (let i = 0; i < byteCharacters.length; i++) {
            byteArrays.push(byteCharacters.charCodeAt(i));
          }

          const byteArray = new Uint8Array(byteArrays);
          return new Blob([byteArray], { type: contentType });
        };
        const linkDocument = document.createElement("a");
        const blob = base64ToBlob(
          response.data[0].document,
          documentInfo.datatype
        );

        const url = window.URL.createObjectURL(blob);
        linkDocument.setAttribute("href", url);
        linkDocument.setAttribute("target", "_blank");
        document.body.appendChild(linkDocument);
        linkDocument.click();
        this.showLoading = false;
      } catch (err) {
        console.log(err);
        this.showLoading = false;
      }
    },
    handleClickOutside(evt) {
      if (this.$refs.filters) {
        if (
          !this.$refs.filters.contains(evt.target) &&
          !this.$refs.showFilters.contains(evt.target)
        ) {
          this.showMobileFilters = false;
        }
      }
    }
  },
  watch: {
    filters() {
      this.filterDocuments();
    }
  },
  async created() {
    this.showLoading = true;
    try {
      await this.$store.dispatch("initAllForms");

      let allDocumentsInfo = await this.$store.dispatch("getAllDocumentsInfo");
      this.$store.state.documents = allDocumentsInfo.data;

      let docsYears = allDocumentsInfo.data.map(d => Number(d.year));
      let docYearsReduced = [...new Set(docsYears)]
        .sort((a, b) => b - a)
        .map(y => {
          return {
            value: y,
            key: y,
            labelColor: "#ffffff"
          };
        });
      this.$store.state.filters.forEach(filter => {
        if (filter.filterKey === "year") {
          filter.inputChilds = docYearsReduced;
        }
      });
      this.showLoading = false;
      this.filterDocuments();
    } catch (err) {
      console.log(err);
      this.showLoading = false;
    }
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
  },
  destroyed() {
    document.removeEventListener("click", this.handleClickOutside);
  }
};
</script>

<style lang="scss">
@import "@/global.scss";
#repository {
  background-color: $black;
  min-height: 100vh;
  padding-bottom: 4em;
  .repo-header {
    border-bottom: 1px solid rgba($color: #fff, $alpha: 0.15);
    .repo-header-inner {
      max-width: 1140px;
      margin: 0 auto;
      padding: 2.5em 0 2em;
      h1 {
        font-size: 34px;
        letter-spacing: 2px;
      }
      .repo-lead {
        max-width: 42em;
        margin: 0.75em auto 0;
        font-size: 14px;
        color: rgba($color: #fff, $alpha: 0.75);
      }
    }
  }
  .repo-container {
    max-width: 1140px;
    margin: 0 auto;
  }
  .b-overlay-wrap {
    display: contents;
    width: 100%;
    height: 100%;
  }
  .filters {
    color: white;
    font-size: 13px;
    text-align: left;
    padding-top: 2.5em;
    padding-right: 1.5em;
    .filters-inner {
      position: sticky;
      top: 1.5em;
      max-height: calc(100vh - 3em);
      overflow-y: auto;
      padding-bottom: 1em;
    }
    .filter-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 1px solid rgba($color: #fff, $alpha: 0.2);
      padding-bottom: 0.75em;
      p {
        letter-spacing: 1px;
      }
      button.clean {
        appearance: none;
        background: transparent;
        border: none;
        color: rgba($color: #fff, $alpha: 0.7);
        font-size: 12px;
        text-decoration: underline;
        cursor: pointer;
        &:hover {
          color: #fff;
        }
      }
    }
    .filter-block {
      padding: 1.25em 0;
      border-bottom: 1px solid rgba($color: #fff, $alpha: 0.1);
    }
    .filter-label {
      font-size: 13px;
      letter-spacing: 1px;
    }
    .filter-option {
      display: flex;
      align-items: center;
      margin-bottom: 0.6em;
      cursor: pointer;
      input {
        position: absolute;
        opacity: 0;
        pointer-events: none;
      }
      .checkbox-mark {
        width: 14px;
        height: 14px;
        border: 1px solid #fff;
        border-radius: 3px;
        margin-right: 0.6em;
        flex-shrink: 0;
        position: relative;
      }
      input:checked + .checkbox-mark {
        background-color: #fff;
        &::after {
          content: "";
          position: absolute;
          left: 4px;
          top: 1px;
          width: 4px;
          height: 8px;
          border: solid $black;
          border-width: 0 2px 2px 0;
          transform: rotate(45deg);
        }
      }
      input:focus + .checkbox-mark {
        box-shadow: 0 0 0 2px rgba($color: #fff, $alpha: 0.4);
      }
      .checkbox-dot {
        width: 9px;
        height: 9px;
        border-radius: 50%;
        margin-right: 0.5em;
        flex-shrink: 0;
      }
      .option-label {
        flex: 1;
      }
      .option-count {
        color: rgba($color: #fff, $alpha: 0.5);
        font-size: 12px;
        margin-left: 0.5em;
      }
    }
    #autocomplete {
      width: 100% !important;
    }
  }
  .documents {
    padding: 2em 0 0 1.5em;
    .toolbar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1em;
      margin-bottom: 1em;
      .search-box {
        position: relative;
        flex: 1;
        max-width: 420px;
        .b-icon {
          position: absolute;
          left: 12px;
          top: 9px;
          color: rgba($color: #fff, $alpha: 0.7);
        }
        input {
          width: 100%;
          background-color: transparent;
          border: 1px solid rgba($color: #fff, $alpha: 0.4);
          border-radius: 5px;
          color: #fff;
          padding: 0.5em 1em 0.5em 2.5em;
          font-size: 13px;
          &::placeholder {
            color: rgba($color: #fff, $alpha: 0.45);
          }
          &:focus {
            outline: none;
            border-color: #fff;
          }
        }
      }
      .results-count {
        font-size: 12px;
        color: rgba($color: #fff, $alpha: 0.7);
        white-space: nowrap;
      }
    }
    .active-filters {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5em;
      margin-bottom: 1.25em;
      .chip {
        display: inline-flex;
        align-items: center;
        gap: 0.45em;
        background-color: rgba($color: #fff, $alpha: 0.08);
        border: 1px solid rgba($color: #fff, $alpha: 0.25);
        border-radius: 999px;
        color: #fff;
        font-size: 12px;
        padding: 0.25em 0.7em;
        cursor: pointer;
        &:hover {
          background-color: rgba($color: #fff, $alpha: 0.16);
        }
        .chip-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
        }
        .b-icon {
          font-size: 13px;
          opacity: 0.7;
        }
      }
    }
    .doc-list {
      min-height: 400px;
    }
    .mobile-filters-row {
      display: none;
    }
    .no-documents {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 320px;
      color: rgba($color: #fff, $alpha: 0.8);
    }
    .doc-row {
      display: flex;
      align-items: stretch;
      background-color: rgba($color: #fff, $alpha: 0.05);
      border: 1px solid rgba($color: #fff, $alpha: 0.08);
      border-radius: 8px;
      margin-bottom: 0.75em;
      overflow: hidden;
      transition: background-color 0.15s ease;
      &:hover {
        background-color: rgba($color: #fff, $alpha: 0.09);
      }
      .doc-color {
        width: 6px;
        flex-shrink: 0;
      }
      .doc-main {
        flex: 1;
        text-align: left;
        padding: 1em 1.25em;
        min-width: 0;
        .doc-badges {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.5em;
          margin-bottom: 0.5em;
          .doc-type {
            color: $black;
            font-size: 11px;
            font-weight: 600;
            border-radius: 3px;
            padding: 0.1em 0.5em;
          }
          .doc-meta {
            font-size: 11px;
            color: rgba($color: #fff, $alpha: 0.65);
            border: 1px solid rgba($color: #fff, $alpha: 0.25);
            border-radius: 3px;
            padding: 0.1em 0.5em;
          }
        }
        .doc-title {
          font-size: 14px;
          word-break: break-word;
        }
        .doc-subtitle {
          font-size: 12px;
          font-style: italic;
          color: rgba($color: #fff, $alpha: 0.65);
          word-break: break-word;
        }
      }
      .doc-actions {
        display: flex;
        align-items: center;
        gap: 0.9em;
        padding: 1em 1.25em;
        border-left: 1px solid rgba($color: #fff, $alpha: 0.08);
        flex-shrink: 0;
        .doc-file {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 1px;
          color: rgba($color: #fff, $alpha: 0.55);
        }
        .doc-download {
          display: inline-flex;
          align-items: center;
          gap: 0.4em;
          appearance: none;
          background: transparent;
          border: 1px solid rgba($color: #fff, $alpha: 0.45);
          border-radius: 5px;
          color: #fff;
          font-size: 12px;
          padding: 0.4em 0.9em;
          cursor: pointer;
          transition: border-color 0.15s ease, background-color 0.15s ease;
          &:hover {
            background-color: rgba($color: #fff, $alpha: 0.12);
            border-color: #fff;
          }
        }
      }
    }
    .pagination {
      justify-content: center;
      margin-top: 1.5em;
      .page-link {
        border: none;
        color: rgba($color: #fff, $alpha: 0.7);
        font-weight: 700;
        background-color: transparent;
        box-shadow: none;
        font-size: 13px;
        &:hover {
          background-color: rgba($color: #fff, $alpha: 0.1);
          color: #fff;
        }
        .b-icon {
          color: #fff;
          font-size: 18px;
        }
      }
      .page-item.active .page-link {
        background-color: #fff;
        color: $black;
        .b-icon {
          color: $black;
        }
      }
      .page-item.disabled .page-link,
      .page-link:disabled {
        color: rgba($color: #fff, $alpha: 0.25);
        .b-icon {
          color: rgba($color: #fff, $alpha: 0.25);
        }
      }
      .left-page,
      .right-page {
        padding: 8px 10px;
      }
    }
  }
}
@media (max-width: 767.98px) {
  #repository {
    .repo-header {
      .repo-header-inner {
        padding: 1.75em 0 1.25em;
        h1 {
          font-size: 26px;
        }
      }
    }
    .filters {
      display: none;
      padding: 1.25em;
      border: 1px solid rgba($color: #fff, $alpha: 0.25);
      border-radius: 5px;
      margin: 0 1em 1.25em;
      &.open {
        display: block;
      }
      .filters-inner {
        position: static;
        max-height: none;
        overflow: visible;
      }
    }
    .documents {
      padding: 1.25em 1em 0;
      .toolbar {
        flex-direction: column;
        align-items: stretch;
        .search-box {
          max-width: none;
        }
        .results-count {
          white-space: normal;
        }
      }
      .mobile-filters-row {
        display: flex;
        justify-content: flex-end;
        margin-bottom: 1em;
        button.filters-toggle {
          appearance: none;
          background-color: transparent;
          color: white;
          border: 1px solid rgba($color: #fff, $alpha: 0.45);
          border-radius: 5px;
          padding: 0.35em 0.8em;
          font-size: 13px;
          span {
            vertical-align: text-bottom;
            margin-right: 0.3em;
          }
        }
      }
      .doc-row {
        flex-wrap: wrap;
        .doc-main {
          padding: 0.9em 1em;
        }
        .doc-actions {
          width: 100%;
          justify-content: space-between;
          border-left: none;
          border-top: 1px solid rgba($color: #fff, $alpha: 0.08);
          padding: 0.7em 1em;
        }
      }
    }
  }
}
</style>
